import { getApiHost } from "#/shared/api/api-host";

/**
 * Loads custom elements config for the effective API origin from inline page data or the config
 * endpoint, then deep-merges `window.Sparkle.ElementsConfig` over it.
 *
 * @returns {Promise<Object>} The config
 * @throws {Error} If config loading fails.
 */
export async function loadConfig() {
  try {
    const { apiHost } = getApiHost();
    const config = readInlineConfig(apiHost) ?? (await fetchConfig(apiHost));

    return mergeConfig(config, globalThis.Sparkle?.ElementsConfig);
  } catch (cause) {
    throw new Error("Failed to load config", { cause });
  }
}

/**
 * Returns the first inline config object matching the effective API origin, or `undefined` when none matches.
 * A null `apiHost` uses the document origin.
 *
 * @param {string|null} apiHost
 * @returns {Object|undefined}
 */
function readInlineConfig(apiHost) {
  const origin = new URL(apiHost ?? location.href).origin;

  for (const script of document.querySelectorAll('script[type="application/json"][data-sp-components]')) {
    try {
      const entry = JSON.parse(script.textContent);

      if (
        entry &&
        new URL(entry.origin).origin === origin &&
        entry.config &&
        typeof entry.config === "object" &&
        !Array.isArray(entry.config)
      ) {
        return entry.config;
      }
    } catch {
      // Malformed inline config leaves the fetch fallback available.
    }
  }
}

/**
 * Deep-merges the override into the base config. Plain objects merge
 * recursively; every other value (arrays included) is replaced.
 *
 * @private
 * @param {Object} base
 * @param {Object|undefined} overrides
 * @returns {Object} The merged config
 */
function mergeConfig(base, overrides) {
  const result = { ...base };

  for (const [key, value] of Object.entries(overrides ?? {})) {
    if (value && typeof value === "object" && !Array.isArray(value) && typeof result[key] === "object") {
      result[key] = mergeConfig(result[key], value);
    } else {
      result[key] = value;
    }
  }

  return result;
}

/**
 * Fetches the custom elements config from the script origin.
 * The request will timeout after 20 seconds.
 *
 * @private
 * @returns {Promise<Object>} The config
 * @throws {Error} If the response is not ok
 */
async function fetchConfig(apiHost) {
  const path = "/ui/components/configuration.json";
  const timeout = 20_000;
  // Without a bundle origin the path is left relative, for the document to resolve. `new URL` rejects a
  // null base rather than ignoring it, so passing the host straight through would throw here instead.
  const url = apiHost ? new URL(path, apiHost) : path;
  const signal = AbortSignal.timeout(timeout);
  const credentials = "include";
  const response = await fetch(url, { signal, credentials });

  if (response.ok) {
    return await response.json();
  }

  throw new Error("Failed to fetch config", { cause: response.statusText });
}
